/**
 * Internal share links — the seller's own way into their own document.
 *
 * A seller who opens a document they are about to send to a buyer must
 * not land on the buyer's credential. Where they used to (the Decision
 * Room's `/preview-token`, which hands back the PROSPECT's distribution
 * token whenever the room has a live one), the rep's reading time, page
 * dwell and completion were written straight onto that buyer's
 * engagement record. An internal link is the structural fix: its views
 * land in `internal_share_views` and the public router that serves it
 * has no code path that can reach `distribution_views`,
 * `distribution_doc_page_views` or `engagement_events` at all.
 *
 * This module holds the *reuse rule*, which is the part that is easy to
 * get subtly wrong, so both callers (the Sales Library asset drawer and
 * the Decision Room document cards) share one implementation of it.
 *
 * Backend contract — `backend/routes/internalShares.js`:
 *   GET  /api/internal-shares?artifactType=&artifactId=   (authed)
 *   POST /api/internal-shares                             (authed)
 *   GET  /api/internal/<token>...                         (token only)
 * Links default to a 14-day expiry (`DEFAULT_EXPIRY_DAYS` in
 * `backend/db/internalShares.js`) and can be revoked at any time.
 */

/**
 * The label a self-preview link is minted under, and the label reuse
 * matches on.
 *
 * Per-USER on purpose. `listForArtifact` scopes rows to the company,
 * not to the creator, so matching on a shared constant like "Preview"
 * would hand every rep at the company the SAME link — and internal
 * share views are counted per link, so two reps reading the same deck
 * would fold into one view trail and neither could tell their own
 * reads from their colleague's. It also keeps a colleague-facing link
 * ("Preview for Sam", minted by hand from the share panel) from being
 * silently absorbed into a rep's own re-reads: an exact-string match
 * only ever reuses a link this same rep minted for this same purpose.
 *
 * 120 chars is the server-side cap on `recipient_label`; slicing here
 * keeps the string we MATCH on identical to the one that was STORED,
 * which a long name would otherwise break.
 */
export function selfPreviewLabel(userName) {
  return `Preview — ${userName || 'me'}`.slice(0, 120);
}

/**
 * Don't hand back a link that is about to die under the reader. An
 * expiry a few seconds out passes a naive `> Date.now()` check and then
 * 410s mid-scroll — and because the tab has already navigated, there is
 * nothing left to retry with. Five minutes is comfortably longer than
 * the round trip and far shorter than the 14-day default, so in
 * practice this only ever rejects links that were already unusable.
 */
const MIN_REMAINING_MS = 5 * 60 * 1000;

/** Is this share row usable right now (not revoked, not near expiry)? */
export function isReusableShare(share) {
  if (!share || share.revokedAt) return false;
  const expiresAt = new Date(share.expiresAt).getTime();
  // NaN (missing/garbage expiry) fails this comparison, which is the
  // answer we want: treat an expiry we cannot read as unusable.
  return expiresAt - Date.now() > MIN_REMAINING_MS;
}

/**
 * Token for `recipientLabel`'s internal link to one artifact, reusing a
 * live one when there is one and minting otherwise.
 *
 * Listing first is what makes this idempotent: without it every click
 * (and every card render) would create a row and an audit event. The
 * list is only an OPTIMIZATION though — if it fails we mint, because a
 * duplicate link is a far better outcome than a dead View button.
 *
 * Throws with a message fit to show a seller. Callers decide whether
 * that surfaces as an error banner or a disabled control.
 */
export async function resolveInternalShareToken({
  apiBase,
  authToken,
  artifactType,
  artifactId,
  recipientLabel,
  signal,
}) {
  const auth = { Authorization: `Bearer ${authToken}` };
  const query = `artifactType=${encodeURIComponent(artifactType)}`
    + `&artifactId=${encodeURIComponent(artifactId)}`;

  try {
    const r = await fetch(`${apiBase}/api/internal-shares?${query}`, { headers: auth, signal });
    if (r.ok) {
      const body = await r.json().catch(() => ({}));
      const reusable = (body.shares || [])
        .filter((s) => isReusableShare(s) && String(s.recipientLabel || '') === recipientLabel)
        // Longest-lived first, so repeated previews settle on one link
        // rather than rotating through several near-expiry ones.
        .sort((a, b) => new Date(b.expiresAt) - new Date(a.expiresAt))[0];
      if (reusable && reusable.token) return reusable.token;
    }
  } catch (err) {
    if (err && err.name === 'AbortError') throw err;
    // Otherwise fall through and mint.
  }

  const r = await fetch(`${apiBase}/api/internal-shares`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...auth },
    body: JSON.stringify({ artifactType, artifactId, recipientLabel }),
    signal,
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.error || `Could not open preview (${r.status})`);
  const token = body.share && body.share.token;
  if (!token) throw new Error('Could not open preview');
  return token;
}

/** The colleague-facing page for a token: `/i/<token>` on this origin. */
export function internalShareUrl(token) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/i/${token}`;
}

/** Base URL for the token-gated document routes behind one link. */
export function internalDocBase(apiBase, token) {
  return `${apiBase}/api/internal/${encodeURIComponent(token)}/doc`;
}
